import './PropertyMap.css';

import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import { AdvancedMarker, InfoWindow, Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSupercluster from 'use-supercluster';

import { CloseButton } from '@/components/ds';
import { AdminProperty } from '@/services/api/admin';
import { GOOGLE_MAPS_MAP_ID } from '@/types/maps';

const MAP_ID = 'property-admin-map';
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;

function MapTypeListener({ onChange }: { onChange: (isDark: boolean) => void }) {
  const map = useMap(MAP_ID);
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener('maptypeid_changed', () => {
      const t = map.getMapTypeId();
      onChange(t === 'satellite' || t === 'hybrid');
    });
    return () => google.maps.event.removeListener(listener);
  }, [map, onChange]);
  return null;
}

interface PropertyMapProps {
  properties: AdminProperty[];
  searchQuery?: string;
  onPropertySelect?: (property: AdminProperty) => void;
}

interface PropertyPoint {
  type: 'Feature';
  properties: { cluster: false; propertyId: string; property: AdminProperty };
  geometry: { type: 'Point'; coordinates: [number, number] };
}

interface GeocodedProperty extends AdminProperty {
  latitude: number;
  longitude: number;
  geocoded?: boolean;
}

// Inner component — uses useMap() which requires rendering inside <Map>
function PropertyMapContent({
  geocodedProperties,
  searchQuery,
  geocoder,
  bounds,
  zoom,
  onPropertySelect,
}: {
  geocodedProperties: GeocodedProperty[];
  searchQuery?: string;
  geocoder: google.maps.Geocoder | null;
  bounds: [number, number, number, number] | undefined;
  zoom: number;
  onPropertySelect?: (property: AdminProperty) => void;
}) {
  const map = useMap(MAP_ID);
  const [selectedProperty, setSelectedProperty] = useState<AdminProperty | null>(null);

  // Fly to searched location when search query changes
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 3 || !geocoder || !map) return;
    const timer = setTimeout(() => {
      geocoder.geocode({ address: searchQuery }, (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) return;
        const viewport = results[0].geometry.viewport;
        if (viewport) {
          map.fitBounds(viewport);
        } else {
          map.panTo(results[0].geometry.location);
          map.setZoom(12);
        }
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, geocoder, map]);

  const points: PropertyPoint[] = useMemo(
    () =>
      geocodedProperties.map((p) => ({
        type: 'Feature',
        properties: { cluster: false as const, propertyId: p.id, property: p },
        geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
      })),
    [geocodedProperties],
  );

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 75, maxZoom: 17 },
  });

  const handleClusterClick = useCallback(
    (clusterId: number, lat: number, lng: number) => {
      if (!supercluster || !map) return;
      const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(clusterId), 17);
      map.setZoom(expansionZoom);
      map.panTo({ lat, lng });
    },
    [supercluster, map],
  );

  const handleMarkerClick = useCallback(
    (property: AdminProperty) => {
      setSelectedProperty(property);
      onPropertySelect?.(property);
    },
    [onPropertySelect],
  );

  return (
    <>
      {clusters.map((cluster) => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const cp = cluster.properties as {
          cluster: boolean;
          cluster_id?: number;
          point_count?: number;
        };

        if (cp.cluster && cp.cluster_id !== undefined) {
          const clusterId = cp.cluster_id;
          return (
            <AdvancedMarker
              key={`cluster-${clusterId}`}
              position={{ lat: latitude, lng: longitude }}
              onClick={() => handleClusterClick(clusterId, latitude, longitude)}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'primary.main',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--tx-text)',
                  fontWeight: '600',
                  fontSize: 14,
                  cursor: 'pointer',
                  boxShadow: 2,
                  '&:hover': { backgroundColor: 'primary.dark' },
                }}>
                {cp.point_count}
              </Box>
            </AdvancedMarker>
          );
        }

        const property = (cluster as PropertyPoint).properties.property;
        return (
          <AdvancedMarker
            key={`property-${property.id}`}
            position={{ lat: latitude, lng: longitude }}
            onClick={() => handleMarkerClick(property)}>
            <Box
              sx={{
                width: 24,
                height: 24,
                backgroundColor: 'error.main',
                borderRadius: '50% 50% 50% 0',
                transform: 'rotate(-45deg)',
                cursor: 'pointer',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'error.dark',
                  transform: 'rotate(-45deg) scale(1.1)',
                },
              }}
            />
          </AdvancedMarker>
        );
      })}

      {selectedProperty && selectedProperty.latitude && selectedProperty.longitude && (
        <InfoWindow
          position={{ lat: selectedProperty.latitude, lng: selectedProperty.longitude }}
          onCloseClick={() => setSelectedProperty(null)}>
          <Paper sx={{ p: 1.5, minWidth: 200, maxWidth: 300 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, pr: 2 }}>
                {selectedProperty.address}
              </Typography>
              <CloseButton size="sm" label="Close" onClose={() => setSelectedProperty(null)} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {selectedProperty.city}, {selectedProperty.state} {selectedProperty.zipCode}
            </Typography>
            {'geocoded' in selectedProperty && selectedProperty.geocoded ? (
              <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                Location geocoded from address
              </Typography>
            ) : null}
            {selectedProperty.owner && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={selectedProperty.owner.email}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 11 }}
                />
              </Box>
            )}
          </Paper>
        </InfoWindow>
      )}
    </>
  );
}

export const PropertyMap = ({ properties, searchQuery, onPropertySelect }: PropertyMapProps) => {
  const mapsLib = useMapsLibrary('maps');
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const [isSatelliteView, setIsSatelliteView] = useState(false);
  const [geocodedProperties, setGeocodedProperties] = useState<GeocodedProperty[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [bounds, setBounds] = useState<[number, number, number, number] | undefined>(undefined);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  useEffect(() => {
    if (mapsLib) setGeocoder(new google.maps.Geocoder());
  }, [mapsLib]);

  // Batch geocode properties that don't have coordinates, with concurrency limit
  useEffect(() => {
    if (!geocoder) return;

    const withCoords: GeocodedProperty[] = properties
      .filter((p) => p.latitude != null && p.longitude != null)
      .map((p) => ({ ...p, latitude: p.latitude as number, longitude: p.longitude as number }));

    const withoutCoords = properties.filter((p) => p.latitude == null || p.longitude == null);

    if (withoutCoords.length === 0) {
      setGeocodedProperties(withCoords);
      return;
    }

    setIsGeocoding(true);
    const geocoded: GeocodedProperty[] = [...withCoords];
    let cancelled = false;

    const BATCH_SIZE = 5;
    const BATCH_DELAY_MS = 250;

    const processBatch = async (batch: typeof withoutCoords) => {
      if (cancelled) return;
      await Promise.all(
        batch.map(
          (property) =>
            new Promise<void>((resolve) => {
              const addressQuery = `${property.address}, ${property.city}, ${property.state} ${property.zipCode}`;
              geocoder.geocode({ address: addressQuery }, (results, status) => {
                if (!cancelled && status === google.maps.GeocoderStatus.OK && results?.[0]) {
                  const loc = results[0].geometry.location;
                  geocoded.push({
                    ...property,
                    latitude: loc.lat(),
                    longitude: loc.lng(),
                    geocoded: true,
                  });
                }
                resolve();
              });
            }),
        ),
      );
    };

    (async () => {
      for (let i = 0; i < withoutCoords.length; i += BATCH_SIZE) {
        if (cancelled) break;
        await processBatch(withoutCoords.slice(i, i + BATCH_SIZE));
        if (i + BATCH_SIZE < withoutCoords.length && !cancelled) {
          await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS));
        }
      }
      if (!cancelled) {
        setGeocodedProperties([...geocoded]);
        setIsGeocoding(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [properties, geocoder]);

  return (
    <Box sx={{ height: '100%', position: 'relative' }}>
      <style>{`
        .gm-style .gm-style-mtc > button,
        .gm-style .gm-fullscreen-control,
        .gm-style .gm-svpc,
        .gm-style .gm-control-active {
          border-radius: 10px !important;
          background: rgba(255,255,255,0.25) !important;
          backdrop-filter: blur(12px) saturate(160%) !important;
          -webkit-backdrop-filter: blur(12px) saturate(160%) !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.18) !important;
          color: ${isSatelliteView ? '#f3f3f3' : '#333333'} !important;
        }
        .gm-style .gm-style-mtc > button {
          font-family: 'Manrope', sans-serif !important;
          font-size: 16px !important;
          font-weight: 400 !important;
          letter-spacing: -0.01em !important;
          text-transform: none !important;
        }
        .gm-style .gm-style-mtc > button[aria-checked="true"],
        .gm-style .gm-style-mtc > button[aria-pressed="true"] {
          background: #333333 !important;
          color: #f3f3f3 !important;
        }
        .gm-style .gm-style-mtc > button svg,
        .gm-style .gm-fullscreen-control svg,
        .gm-style .gm-control-active svg {
          fill: ${isSatelliteView ? '#f3f3f3' : '#333333'} !important;
          stroke: ${isSatelliteView ? '#f3f3f3' : '#333333'} !important;
        }
        .gm-style .gm-fullscreen-control img,
        .gm-style .gm-control-active img {
          filter: ${isSatelliteView ? 'brightness(0) invert(1)' : 'brightness(0)'} !important;
        }
        .gm-style .gm-svpc img {
          filter: ${isSatelliteView ? 'grayscale(1) brightness(1.4)' : 'grayscale(1) brightness(0.6)'} !important;
        }
        .gm-style .gm-style-mtc + .gm-style-mtc {
          margin-left: 5px !important;
        }
        .gm-style .gm-style-mtc ul,
        .gm-style .gm-style-mtc-menu {
          border-radius: 10px !important;
          background: rgba(255,255,255,0.25) !important;
          backdrop-filter: blur(12px) saturate(160%) !important;
          -webkit-backdrop-filter: blur(12px) saturate(160%) !important;
          box-shadow: 0 4px 14px rgba(0,0,0,0.2) !important;
          padding: 4px !important;
          border: none !important;
          left: 0 !important;
          right: auto !important;
        }
        .gm-style .gm-style-mtc ul li,
        .gm-style .gm-style-mtc ul li *,
        .gm-style .gm-style-mtc-menu * {
          font-size: 12px !important;
          color: #333333 !important;
          font-family: 'Manrope', sans-serif !important;
        }
        .gm-style .gm-style-mtc ul li {
          border-radius: 6px !important;
          display: flex !important;
          align-items: center !important;
          gap: 2.5px !important;
        }
        .gm-style .gm-style-mtc ul li > * + * {
          margin-left: 2.5px !important;
        }
      `}</style>
      <Map
        id={MAP_ID}
        mapId={GOOGLE_MAPS_MAP_ID}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        style={{ width: '100%', height: '100%' }}
        gestureHandling="greedy"
        disableDefaultUI={false}
        onBoundsChanged={(ev) => {
          const { north, south, east, west } = ev.detail.bounds;
          setBounds([west, south, east, north]);
          setZoom(ev.detail.zoom);
        }}>
        <MapTypeListener onChange={setIsSatelliteView} />
        <PropertyMapContent
          geocodedProperties={geocodedProperties}
          searchQuery={searchQuery}
          geocoder={geocoder}
          bounds={bounds}
          zoom={zoom}
          onPropertySelect={onPropertySelect}
        />
      </Map>

      {isGeocoding && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            p: 1.5,
            backgroundColor: 'info.light',
            maxWidth: 300,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
          <CircularProgress size={16} />
          <Typography variant="caption">Geocoding property addresses...</Typography>
        </Paper>
      )}

      {!isGeocoding && geocodedProperties.length < properties.length && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            p: 1.5,
            backgroundColor: 'warning.light',
            maxWidth: 300,
          }}>
          <Typography variant="caption">
            {properties.length - geocodedProperties.length} of {properties.length}{' '}
            {properties.length === 1 ? 'property' : 'properties'} could not be geocoded and{' '}
            {properties.length - geocodedProperties.length === 1 ? 'is' : 'are'} not shown on the
            map.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
